import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Mail, ExternalLink } from 'lucide-react'
import logo from '../assets/solidlabs-logo.png'
import PrivacyModal from './PrivacyModal'
import { useT } from '../i18n/LanguageContext'

const productLinks = [
  { label: 'Spotto App', href: 'https://spotto.pl', external: true },
  {
    label: 'Google Play',
    href: 'https://play.google.com/store/apps/details?id=pl.spotto.mobile',
    external: true,
  },
  { label: 'App Store', href: 'https://apps.apple.com/app/spotto', external: true },
]

export default function Footer() {
  const t = useT()
  const [privacyOpen, setPrivacyOpen] = useState(false)

  // Company nav links — reuse keys from nav dictionary (same labels).
  // Pełne ścieżki ('/#about'), bo stopka renderuje się także na podstronach bloga,
  // gdzie sama kotwica nie miałaby do czego trafić.
  const companyLinks = [
    { label: t.nav.about, to: '/#about' },
    { label: t.nav.product, to: '/#product' },
    { label: t.nav.oferta, to: '/#oferta' },
    { label: t.nav.realizacje, to: '/#realizacje' },
    { label: t.nav.blog, to: '/blog' },
    { label: t.nav.contact, to: '/#contact' },
  ]

  return (
    <>
      <footer id="contact" className="relative border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Company info */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img
                  src={logo}
                  alt={t.nav.logoAlt}
                  className="h-16 w-auto brightness-0 invert opacity-90"
                />
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed max-w-sm mb-6">
                {t.footer.descriptionLine1}
                <br />
                {t.footer.descriptionLine2}
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-base text-zinc-300">
                  <MapPin size={18} className="text-zinc-500 shrink-0" />
                  Mogilska 43, 31-545 Kraków
                </div>
                <a
                  href="mailto:hello@solidlabs.site"
                  className="flex items-center gap-3 text-base text-zinc-300 hover:text-brand-400 transition-colors"
                >
                  <Mail size={18} className="text-zinc-500 shrink-0" />
                  hello@solidlabs.site
                </a>
              </div>

              {/* Legal company info */}
              <div className="max-w-sm pt-5 border-t border-white/[0.04] space-y-1 text-sm text-zinc-400">
                <p className="text-zinc-200 font-semibold text-base">Solid Labs PSA</p>
                <p className="font-mono uppercase tracking-[0.15em] text-xs text-zinc-500 pb-2">
                  {t.footer.legalForm}
                </p>
                <p>KRS: 0000987172</p>
                <p>NIP: 6751768986</p>
                <p>REGON: 522842730</p>
              </div>
            </div>

            {/* Product links */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-5">
                {t.footer.productHeader}
              </h4>
              <ul className="space-y-3">
                {productLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      {link.label}
                      {link.external && <ExternalLink size={12} />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company links */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-5">
                {t.footer.companyHeader}
              </h4>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-zinc-600">
              &copy; {new Date().getFullYear()} Solid Labs PSA.{' '}
              {t.footer.allRightsReserved}
            </p>
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={() => setPrivacyOpen(true)}
                className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors cursor-pointer"
              >
                {t.footer.privacyPolicy}
              </button>
            </div>
          </div>
        </div>
      </footer>

      <PrivacyModal
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      />
    </>
  )
}
